import type { DeletedPaymentDetails, PaymentDetailsDiscrepancies } from '@approvalmax/types';
import { openSplitViewState } from '@approvalmax/ui/src/components';
import { useAtom, useSetAtom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import { domain } from 'modules/data';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { checkPaymentDetailsHasConflictingVersions } from './XeroAmaxPayBatchPaymentForm.helpers';
import {
    usePrepopulateSelectedBankAccountFromLines,
    useSaveAndSubmitMutation,
} from './XeroAmaxPayBatchPaymentForm.hooks';
import {
    SelectedSimplifiedBankAccount,
    XeroAmaxPayBatchPaymentFormContextProviderProps,
    XeroAmaxPayBatchPaymentFormContextType,
} from './XeroAmaxPayBatchPaymentForm.types';

const XeroAmaxPayBatchPaymentFormContext = React.createContext<XeroAmaxPayBatchPaymentFormContextType>(null as any);

export const XeroAmaxPayBatchPaymentFormContextProvider: FC<XeroAmaxPayBatchPaymentFormContextProviderProps> = (
    props
) => {
    const { children, editRequestField, editRequestFieldImmediately, request } = props;

    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const [openSplitView, setOpenSplitView] = useAtom(openSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);

    const [billBatchPayment, setBillBatchPayment] = useState<domain.XeroAmaxPayBatchPayment>(
        cloneDeep(request.details) || {
            items: [],
            isMergedBySupplier: false,
            referencesForMergedItems: {},
        }
    );
    const [selectedSimplifiedBankAccount, setSelectedSimplifiedBankAccount] = useState<
        SelectedSimplifiedBankAccount | undefined
    >();

    const [showErrors, setShowErrors] = useState(false);
    const [billInvoiceRequestIdsWithExceededAmount, setBillInvoiceRequestIdsWithExceededAmount] = useState<string[]>(
        []
    );
    const [billInvoiceRequestIdsWithError, setBillInvoiceRequestIdsWithError] = useState<string[]>([]);
    const [deletedPaymentDetails, setDeletedPaymentDetails] = useState<DeletedPaymentDetails[]>([]);
    const [paymentDetailsDiscrepancies, setPaymentDetailsDiscrepancies] = useState<PaymentDetailsDiscrepancies[]>([]);

    const firstBillInBatch = billBatchPayment.items[0];

    usePrepopulateSelectedBankAccountFromLines({
        selectedSimplifiedBankAccount,
        setSelectedSimplifiedBankAccount,
        bankAccountFromBill: firstBillInBatch?.amaxPayBankAccount
            ? {
                  id: firstBillInBatch?.amaxPayBankAccount.id,
                  name: firstBillInBatch?.amaxPayBankAccount?.text,
              }
            : undefined,
    });

    const { saveAndSubmit, isSaving } = useSaveAndSubmitMutation({
        xeroAmaxPayBatchPayment: billBatchPayment,
        request,
        setBillInvoiceRequestIdsWithExceededAmount,
        setBillInvoiceRequestIdsWithError,
        setDeletedPaymentDetails,
        setPaymentDetailsDiscrepancies,
    });

    const removeSelectableBillById = useCallback(
        (xeroBillInvoiceRequestId: string) => {
            const newItems = billBatchPayment.items.filter(
                (bill) => bill.xeroBillInvoiceRequestId !== xeroBillInvoiceRequestId
            );

            const newIsMergedBySupplier = billBatchPayment.isMergedBySupplier
                ? !checkPaymentDetailsHasConflictingVersions(newItems)
                : false;

            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                isMergedBySupplier: newIsMergedBySupplier,
                items: newItems,
            }));

            if (billBatchPayment.isMergedBySupplier !== newIsMergedBySupplier) {
                editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>(
                    'isMergedBySupplier',
                    newIsMergedBySupplier
                );
            }

            editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>('items', newItems);
        },
        [billBatchPayment.isMergedBySupplier, billBatchPayment.items, editRequestFieldImmediately]
    );

    const changeIsMergedBySupplier = useCallback(
        (newValue: boolean) => {
            if (showErrors) {
                setShowErrors(false);
            }

            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                isMergedBySupplier: newValue,
            }));
            editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>('isMergedBySupplier', newValue);
        },
        [editRequestFieldImmediately, showErrors]
    );

    const changeReferencesForMergedItems = useCallback(
        (newValue: Record<string, string>) => {
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                referencesForMergedItems: {
                    ...prevBillBatchPayment.referencesForMergedItems,
                    ...newValue,
                },
            }));
            editRequestField<domain.XeroAmaxPayBatchPaymentDetails>('referencesForMergedItems', newValue);
        },
        [editRequestField]
    );

    const changeBillInvoiceItemField = useCallback(
        (
            xeroBillInvoiceRequestId: string,
            itemField: keyof domain.AmaxPayPaymentItem,
            newValue: domain.AmaxPayPaymentItem[keyof domain.AmaxPayPaymentItem]
        ) => {
            const newItems = billBatchPayment.items.map((bill) => ({
                ...bill,
                [itemField]: bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId ? newValue : bill[itemField],
            }));

            editRequestField<domain.XeroAmaxPayBatchPaymentDetails>('items', newItems);
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));
        },
        [billBatchPayment.items, editRequestField]
    );

    const changePaymentDetails = useCallback(
        (xeroBillInvoiceRequestId: string, paymentDetails: domain.PaymentDetails | null) => {
            const newItems = billBatchPayment.items.map((bill) => ({
                ...bill,
                paymentDetails:
                    xeroBillInvoiceRequestId === bill.xeroBillInvoiceRequestId ? paymentDetails : bill.paymentDetails,
            }));

            const newIsMergedBySupplier = billBatchPayment.isMergedBySupplier
                ? !checkPaymentDetailsHasConflictingVersions(newItems)
                : false;

            editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>('items', newItems);

            if (billBatchPayment.isMergedBySupplier !== newIsMergedBySupplier) {
                editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>(
                    'isMergedBySupplier',
                    newIsMergedBySupplier
                );
            }

            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
                isMergedBySupplier: newIsMergedBySupplier,
            }));
        },
        [billBatchPayment.isMergedBySupplier, billBatchPayment.items, editRequestFieldImmediately]
    );

    const applyPaymentDetailsDiscrepancies = useCallback(() => {
        const newItems = billBatchPayment.items.map((item) => {
            const newPaymentDetails = paymentDetailsDiscrepancies.find(
                (paymentDetailsDiscrepancy) => paymentDetailsDiscrepancy.id === item.paymentDetails?.id
            );

            return {
                ...item,
                paymentDetails: item.paymentDetails
                    ? {
                          ...item.paymentDetails,
                          versionId: newPaymentDetails ? newPaymentDetails.versionId : item.paymentDetails.versionId,
                      }
                    : null,
            };
        });
        const newBillBitchPayment = {
            ...billBatchPayment,
            items: newItems,
        };

        setBillBatchPayment(newBillBitchPayment);

        saveAndSubmit(undefined, newBillBitchPayment);
    }, [billBatchPayment, paymentDetailsDiscrepancies, saveAndSubmit]);

    const addBills = useCallback(
        (selectableBills: domain.AmaxPayBatchSelectableBillItem[]) => {
            const newItems = [
                ...billBatchPayment.items,
                ...selectableBills
                    .filter((bill) => bill.selected)
                    .map((bill) => ({
                        ...bill,
                        amaxPayBankAccount: selectedSimplifiedBankAccount
                            ? {
                                  id: selectedSimplifiedBankAccount.id,
                                  text: selectedSimplifiedBankAccount.name,
                              }
                            : bill.amaxPayBankAccount,
                    })),
            ];

            const newIsMergedBySupplier = billBatchPayment.isMergedBySupplier
                ? !checkPaymentDetailsHasConflictingVersions(newItems)
                : false;

            setBillBatchPayment({
                ...billBatchPayment,
                items: newItems,
                isMergedBySupplier: newIsMergedBySupplier,
            });

            if (billBatchPayment.isMergedBySupplier !== newIsMergedBySupplier) {
                editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>(
                    'isMergedBySupplier',
                    newIsMergedBySupplier
                );
            }

            editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>('items', newItems);
        },
        [billBatchPayment, editRequestFieldImmediately, selectedSimplifiedBankAccount]
    );

    const setBatchPaymentAndUpdateRequestDetails = useCallback(
        (
            newBatchPayment: domain.XeroAmaxPayBatchPaymentDetails,
            propName: keyof domain.XeroAmaxPayBatchPaymentDetails
        ) => {
            const newIsMergedBySupplier = newBatchPayment.isMergedBySupplier
                ? !checkPaymentDetailsHasConflictingVersions(newBatchPayment.items)
                : false;

            setBillBatchPayment({ ...newBatchPayment, isMergedBySupplier: newIsMergedBySupplier });

            if (billBatchPayment.isMergedBySupplier !== newIsMergedBySupplier) {
                editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>(
                    'isMergedBySupplier',
                    newIsMergedBySupplier
                );
            }

            editRequestFieldImmediately<domain.XeroAmaxPayBatchPaymentDetails>(propName, newBatchPayment[propName]);
        },
        [billBatchPayment.isMergedBySupplier, editRequestFieldImmediately]
    );

    const clearItems = useCallback(() => {
        setBillBatchPayment((prevBillBatchPayment) => ({
            ...prevBillBatchPayment,
            items: [],
        }));

        editRequestField<domain.XeroAmaxPayBatchPaymentDetails>('items', []);

        if (
            openSplitView &&
            (contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment ||
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment)
        ) {
            setOpenSplitView(false);
            setContentSplitView({ mode: ContentSplitViewMode.Empty });
            setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
        }
    }, [
        contentSplitView.mode,
        editRequestField,
        openSplitView,
        setContentSplitView,
        setSplitViewGoToBarSettings,
        setOpenSplitView,
    ]);

    const value = useMemo(
        () => ({
            billBatchPayment,
            setBillBatchPayment: setBatchPaymentAndUpdateRequestDetails,
            showErrors,
            setShowErrors,
            saveAndSubmit,
            clearItems,
            addBills,
            removeSelectableBillById,
            changeBillInvoiceItemField,
            changePaymentDetails,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            isSaving,
            companyId: request.companyId,
            companyCurrency: request.companyCurrency,
            selectedSimplifiedBankAccount,
            setSelectedSimplifiedBankAccount,
            deletedPaymentDetails,
            setDeletedPaymentDetails,
            paymentDetailsDiscrepancies,
            setPaymentDetailsDiscrepancies,
            applyPaymentDetailsDiscrepancies,
            referencesForMergedItems: billBatchPayment.referencesForMergedItems || {},
            changeReferencesForMergedItems,
            changeIsMergedBySupplier,
        }),
        [
            billBatchPayment,
            setBatchPaymentAndUpdateRequestDetails,
            showErrors,
            saveAndSubmit,
            clearItems,
            addBills,
            removeSelectableBillById,
            changeBillInvoiceItemField,
            changePaymentDetails,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            isSaving,
            request.companyId,
            request.companyCurrency,
            selectedSimplifiedBankAccount,
            setSelectedSimplifiedBankAccount,
            deletedPaymentDetails,
            setDeletedPaymentDetails,
            paymentDetailsDiscrepancies,
            setPaymentDetailsDiscrepancies,
            applyPaymentDetailsDiscrepancies,
            changeReferencesForMergedItems,
            changeIsMergedBySupplier,
        ]
    );

    return (
        <XeroAmaxPayBatchPaymentFormContext.Provider value={value}>
            {children}
        </XeroAmaxPayBatchPaymentFormContext.Provider>
    );
};

export function useXeroAmaxPayBatchPaymentFormContext() {
    return useContext(XeroAmaxPayBatchPaymentFormContext);
}
